import React, { useState, useRef, useEffect } from 'react';
import { IconButton, Menu, MenuItem, Divider, Typography } from '@mui/material';
import {
  FullscreenRounded,
  LoopRounded,
  SubtitlesRounded,
  CastRounded,
  VolumeUpRounded,
  VolumeDownRounded,
  MoreVertRounded,
} from '@mui/icons-material';
import { Tooltip } from 'src/shared/ui/tooltip';

interface SettingsControlProps {
  isMobile: boolean;
  playbackSpeed: number;
  isFullscreen: boolean;
  loopEnabled: boolean;
  volume: number;
  handlePlaybackSpeedChange: () => void;
  handleFullscreen: () => void;
  openLoopModal: () => void;
  clearLoop: () => void;
  toggleMute: () => void;
}
const isIOSSafari = (): boolean => {
  if (typeof window === 'undefined') return false;
  const userAgent = window.navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(userAgent);
  const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
  return isIOS && isSafari;
};
export const SettingsControl: React.FC<SettingsControlProps> = ({
  isMobile,
  playbackSpeed,
  isFullscreen,
  loopEnabled,
  volume,
  handlePlaybackSpeedChange,
  handleFullscreen,
  openLoopModal,
  clearLoop,
  toggleMute,
}) => {
  // States for both implementations
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [showCustomMenu, setShowCustomMenu] = useState(false);
  const menuContainerRef = useRef<HTMLDivElement>(null);

  // Close custom menu when clicking outside (fullscreen only)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuContainerRef.current && !menuContainerRef.current.contains(event.target as Node)) {
        setShowCustomMenu(false);
      }
    };

    if (showCustomMenu && isFullscreen) {
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCustomMenu, isFullscreen]);

  // Handle menu open/close for both implementations
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (isFullscreen) {
      setShowCustomMenu(!showCustomMenu);
    } else {
      setMenuAnchorEl(event.currentTarget);
    }
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setShowCustomMenu(false);
  };

  // Handle menu item clicks - different behavior for each implementation
  const handleMenuItemClick = (action: () => void, shouldClose = false) => {
    action();
    if (shouldClose || !isFullscreen) {
      handleMenuClose();
    }
  };

  return (
    <>
      {/* Fullscreen button (always visible) */}
      <Tooltip
        content={
          <Typography variant="button-s">
            {isFullscreen
              ? 'Esci Schermo Intero'
              : isIOSSafari()
                ? 'Modalità Cinema'
                : 'Schermo Intero'}
          </Typography>
        }
        portal
      >
        <IconButton onClick={handleFullscreen} className="text-white hover:text-primary">
          <FullscreenRounded />
        </IconButton>
      </Tooltip>
      {/* Mobile menu button - now also shows in fullscreen regardless of device */}
      {(isMobile || isFullscreen) && (
        <div className="relative" ref={menuContainerRef}>
          <IconButton onClick={handleMenuOpen} className="text-white hover:text-primary">
            <MoreVertRounded />
          </IconButton>

          {/* Material-UI Menu for NON-fullscreen */}
          {!isFullscreen && (
            <Menu
              onClick={handleMenuClose}
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              disablePortal={false}
              slotProps={{
                root: {
                  container: document.body,
                },
              }}
            >
              <MenuItem onClick={() => handleMenuItemClick(handlePlaybackSpeedChange)}>
                <Typography variant="s2">Velocità: {playbackSpeed}x</Typography>
              </MenuItem>

              <MenuItem
                onClick={() => handleMenuItemClick(loopEnabled ? clearLoop : openLoopModal)}
              >
                <Typography variant="s2" className="flex items-center gap-2">
                  <LoopRounded />
                  {loopEnabled ? 'Disabilita Loop' : 'Imposta Loop'}
                </Typography>
              </MenuItem>

              <Divider />

              <MenuItem onClick={() => handleMenuItemClick(toggleMute)}>
                <Typography variant="s2" className="flex items-center gap-2">
                  {volume > 0 ? (
                    <>
                      <VolumeUpRounded /> Muta
                    </>
                  ) : (
                    <>
                      <VolumeDownRounded /> Attiva Audio
                    </>
                  )}
                </Typography>
              </MenuItem>
            </Menu>
          )}

          {/* Custom dropdown menu for FULLSCREEN */}
          {isFullscreen && showCustomMenu && (
            <div
              className="absolute right-0 bottom-full mb-2 w-48 rounded-lg shadow-lg overflow-hidden"
              style={{
                zIndex: 1000,
                backgroundColor: '#15151A',
                border: '1px solid rgba(255, 255, 255, 0.12)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="px-4 py-3  hover:bg-opacity-8 cursor-pointer transition-colors"
                style={{
                  WebkitTapHighlightColor: 'transparent',
                  outline: 'none',
                }}
                onClick={() => handleMenuItemClick(handlePlaybackSpeedChange)}
              >
                <Typography variant="s2" className="text-white">
                  Velocità: {playbackSpeed}x
                </Typography>
              </div>

              <div
                className="px-4 py-3  hover:bg-opacity-8 cursor-pointer transition-colors"
                style={{
                  WebkitTapHighlightColor: 'transparent',
                  outline: 'none',
                }}
                onClick={() => handleMenuItemClick(loopEnabled ? clearLoop : openLoopModal)}
              >
                <Typography variant="s2" className="text-white flex items-center gap-2">
                  <LoopRounded className="text-sm" />
                  {loopEnabled ? 'Disabilita Loop' : 'Imposta Loop'}
                </Typography>
              </div>

              <div className="border-t border-gray-600 opacity-50" />

              <div
                className="px-4 py-3  hover:bg-opacity-8 cursor-pointer transition-colors"
                style={{
                  WebkitTapHighlightColor: 'transparent',
                  outline: 'none',
                }}
                onClick={() => handleMenuItemClick(toggleMute)}
              >
                <Typography variant="s2" className="text-white flex items-center gap-2">
                  {volume > 0 ? (
                    <>
                      <VolumeUpRounded className="text-sm" /> Muta
                    </>
                  ) : (
                    <>
                      <VolumeDownRounded className="text-sm" /> Attiva Audio
                    </>
                  )}
                </Typography>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Desktop-only additional controls - removed Sottotitoli and Trasmetti buttons */}
      {!isMobile && !isFullscreen && (
        <>{/* Standalone buttons for Sottotitoli and Trasmetti removed */}</>
      )}
    </>
  );
};
